/**
 * Wagon details component
 */
import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Field, FormErrors, InjectedFormProps, reduxForm, SubmissionError } from 'redux-form';
import { getOfflineState, getVehiclesLibrary, selectSecurityContextRoles } from '../../../commons/selectors/selectors';
import Overlay from '../../../commons/components/overlay/Overlay';
import Input from '../../../commons/components/input/Input';
import { cancelEditWagon, createLibraryWagon, deleteLibraryWagon, updateLibraryWagon } from '../vehiclesLibraryDuck';
import { openConfirmDialog } from '../../../commons/components/modal/confirmModalDucks';
import { addDamageReport } from '../../../damage-reports/damage-report/damageReportDucks';
import { parseToInt } from '../../../commons/redux-form/valueParser';
import {
  normalizePositiveFloatTwoDecimal,
  normalizePositiveNumber,
  normalizeRegistration,
} from '../../../commons/redux-form/valueNormalizer';
import Mask from '../../../commons/mask/Mask';
import { formatDecimalAndSeparateThousands, formatDecimalNumber } from '../../../commons/redux-form/valueFormatter';
import { DAMAGE_REPORT_WRITE, RECOMMISSIONING_REPORT_ADD, REPAIR_WRITE } from '../../../commons/security/userRoles';
import { getWagonGeneralStatus, initialFormValuesForNewWagonTemplates, validate } from './wagonTemplateUtils';

import './WagonDetailsStyles.scss';
import ownerCache from '../../../commons/templates/ownerCache';
import Dropdown from '../../../commons/components/dropdown/Dropdown';
import { useAppDispatch, useAppSelector } from '../../../commons/store/hooks';
import { RootState } from '../../../commons/reducers/rootReducer';
import { Rsrd2WagonTemplate, WagonTemplate } from '../../../commons/model/templates';
import CheckboxFormComponent from '../../../commons/components/checkbox/CheckboxFormComponent';
import { hideOverlay } from '../../../commons/components/overlay/overlayDucks';
import { CompanyOptions } from '../../../commons/model/common';
import { createRepair } from '../../../repairs/RepairDucks';
import { browserHistory } from '../../../commons/routes/routerHistoryConfig';
import Tooltip from 'rc-tooltip';
import WagonDamageReportList from './WagonDamageReportList';
import WagonRecommissioningReportList from './recommissioning/WagonRecommissioningReportList';
import { openRecommissioningReportDialog } from './recommissioning/wagonRecommissioningReportDucks';
import { useViewportWidth } from '../../../commons/responsive/hooks';
import classNames from 'classnames';
import { useWagonTemplateReportsLoader } from './recommissioning/useWagonTemplateReportsLoader';
import helpers from '../../../commons/helpers/helpers';
import { wagonHasRedLabel } from '../vehiclesLibraryUtils';
import apiHelper from '../../../api/apiHelper';

type WagonTemplateFormData = WagonTemplate;

type WagonTemplateDetailsProps = InjectedFormProps<WagonTemplateFormData>;

const WagonTemplateDetails = ({ handleSubmit, change }: WagonTemplateDetailsProps) => {
  const vehiclesLibrary = useAppSelector(getVehiclesLibrary);
  const roles = useAppSelector(selectSecurityContextRoles);
  const dispatch = useAppDispatch();
  const wagonTemplateDetails = useWagonTemplateReportsLoader(vehiclesLibrary.editedWagon?.registration);
  const { isMobile } = useViewportWidth();
  const offline = useSelector(getOfflineState);

  const [ownersOptions, setOwnersOptions] = useState({});

  const persistWagonTemplate = async (updatedWagon: WagonTemplate) => {
    const action = vehiclesLibrary.editedWagon ? updateLibraryWagon(updatedWagon) : createLibraryWagon(updatedWagon);
    const result = await dispatch(action);
    if (result.meta.requestStatus === 'rejected') {
      throw new SubmissionError(result.payload as FormErrors);
    }
  };

  const canCreateNewPVCA = () => {
    if (!wagonTemplateDetails) {
      return false;
    }
    if (wagonTemplateDetails?.inProgressDamageReports?.length === 0) {
      return true;
    }
    // if wagon has RED labels on "active" damageReports, impossible to create new PVCA
    return !wagonHasRedLabel(wagonTemplateDetails);
  };

  const canCreateRepair = () => {
    if (!wagonTemplateDetails) {
      return false;
    }
    // if we want to create a repair on a wagonTemplate, it should not have a repair in progress
    return !wagonTemplateDetails.inProgressRepairId;
  };

  const requestRsrd = (data: WagonTemplate, propertyToUpdate: Partial<WagonTemplate>) => async () => {
    const wagonTemplateAfterUpdate = helpers.with(data, propertyToUpdate);
    //request RSRD2 when online + the registration is changed + the registration number is not empty
    if (
      !offline &&
      (!data?.registration || data.registration !== wagonTemplateAfterUpdate.registration) &&
      wagonTemplateAfterUpdate.registration != null
    ) {
      const registration = helpers.registrationUnformat(wagonTemplateAfterUpdate.registration);
      apiHelper.get(`/api/wagon-templates/rsrd2/${registration}`).then((rsrd2WagonTemplate: Rsrd2WagonTemplate) => {
        change('length', rsrd2WagonTemplate.length);
        change('tare', rsrd2WagonTemplate.tare);
        change('nbAxles', rsrd2WagonTemplate.nbAxles);
        change('ownerId', rsrd2WagonTemplate.ownerId);
      });
    }
  };

  useEffect(() => {
    (async () => {
      const owners = await ownerCache.findAllItems();

      setOwnersOptions(
        owners.reduce(
          (acc, owner) => ({
            ...acc,
            [owner.id]: owner.name,
          }),
          {},
        ),
      );
    })();
  }, []);

  const title = vehiclesLibrary.editedWagon
    ? `Modifier wagon - ${getWagonGeneralStatus(vehiclesLibrary.editedWagon)}`
    : 'Ajouter un wagon';

  return (
    <Overlay name="vehicle-library" title={title} onClosed={() => dispatch(cancelEditWagon())} closeButton>
      <div className={classNames('round-btn-group', isMobile ? 'round-btn-group-mobile' : '')}>
        {!offline && (
          <>
            {roles.includes(RECOMMISSIONING_REPORT_ADD) && (
              <Tooltip overlay="Déclarer une remise en service" placement="top" trigger={['hover', 'click']}>
                <button
                  className="btn btn-round btn-recommissioning-manually"
                  type="button"
                  onClick={() =>
                    dispatch(openRecommissioningReportDialog(vehiclesLibrary.editedWagon, null, 'CREATION'))
                  }
                />
              </Tooltip>
            )}
            {roles.includes(REPAIR_WRITE) && !offline && canCreateRepair() && (
              <Tooltip overlay="Créer une réparation" placement="top" trigger={['hover', 'click']}>
                <button
                  className="btn btn-round btn-new-repair"
                  type="button"
                  onClick={async () => {
                    browserHistory.push('/repairs');
                    dispatch(
                      createRepair({
                        repairDetails: null,
                        initialWagonTemplate: {
                          id: vehiclesLibrary.editedWagon.id,
                          registration: vehiclesLibrary.editedWagon.registration,
                        },
                      }),
                    );
                  }}
                />
              </Tooltip>
            )}
          </>
        )}

        {roles.includes(DAMAGE_REPORT_WRITE) && canCreateNewPVCA() && (
          <Tooltip overlay="Ouvrir un PVCA" placement="top" trigger={['hover', 'click']}>
            <button
              className="btn btn-round btn-new-damage-report"
              type="button"
              onClick={async () => {
                const ownerTemplate =
                  vehiclesLibrary.editedWagon?.ownerId &&
                  (await ownerCache.findItemById(vehiclesLibrary.editedWagon.ownerId));
                dispatch(addDamageReport(true, vehiclesLibrary.editedWagon, ownerTemplate));
              }}
            ></button>
          </Tooltip>
        )}
      </div>
      <form noValidate={true} className="overlay-form" onSubmit={handleSubmit(persistWagonTemplate)}>
        <Field
          disabled={vehiclesLibrary.editedWagon}
          component={Input}
          name="registration"
          labelText="Immatriculation *"
          placeholder="Ex: 12 34 567 8901-2"
          format={(value: string) => value && Mask.REGISTRATION.format(value)}
          inputMode="numeric"
          pattern="[0-9]*"
          normalize={normalizeRegistration}
          saveAction={requestRsrd}
        />
        <div className="inputs-group">
          <Field
            component={Input}
            name="length"
            labelText="Longueur *"
            unit="m"
            format={formatDecimalNumber}
            inputMode="numeric"
            pattern="[0-9]*"
            normalize={normalizePositiveFloatTwoDecimal}
          />
          <Field
            component={Input}
            name="tare"
            labelText="Tare *"
            unit="kg"
            format={formatDecimalAndSeparateThousands}
            inputMode="numeric"
            pattern="[0-9]*"
            normalize={normalizePositiveNumber}
            parse={parseToInt}
          />
        </div>
        <Field
          component={Input}
          name="nbAxles"
          labelText="Nombre d’essieux *"
          inputMode="numeric"
          pattern="[0-9]*"
          normalize={normalizePositiveNumber}
          parse={parseToInt}
        />
        <Field component={Dropdown} labelText="Détenteur *" name="ownerId" options={ownersOptions} readOnly={false} />
        <Field component={CheckboxFormComponent} name="doubleWagon" label="Wagon double" />
        <Field component={Dropdown} labelText="Gestionnaire" name="manager" options={CompanyOptions} />
        <WagonDamageReportList
          registration={vehiclesLibrary.editedWagon?.registration ?? ''}
          damageReports={wagonTemplateDetails?.inProgressDamageReports ?? []}
          canOpenDamageReport={roles.includes(DAMAGE_REPORT_WRITE) && !offline}
        />
        {wagonTemplateDetails?.recommissioningReports && (
          <WagonRecommissioningReportList
            wagonTemplate={vehiclesLibrary.editedWagon}
            reports={wagonTemplateDetails?.recommissioningReports}
          />
        )}

        {wagonTemplateDetails?.createdDateTime && (
          <div className="wagon-audit">
            Créé {wagonTemplateDetails?.createdByName ? ` par ${wagonTemplateDetails.createdByName}, ` : ''} le{' '}
            {helpers.dateTimeFormat(wagonTemplateDetails.createdDateTime)}
          </div>
        )}
        {wagonTemplateDetails?.updatedDateTime && (
          <div className="wagon-audit">
            Mis à jour{wagonTemplateDetails?.updatedByName ? ` par ${wagonTemplateDetails.updatedByName}, ` : ''} le{' '}
            {helpers.dateTimeFormat(wagonTemplateDetails.updatedDateTime)}
          </div>
        )}

        <div className="btn-group wagon-template-form-actions">
          <button className="btn btn-success" type="submit">
            {vehiclesLibrary.editedWagon ? 'Modifier' : 'Ajouter'}
          </button>
          <button
            className="btn btn-link"
            type="button"
            onClick={() => {
              dispatch(hideOverlay());
              dispatch(cancelEditWagon());
            }}
          >
            Annuler
          </button>
          {vehiclesLibrary.editedWagon && (
            <button
              className="btn btn-icon remove"
              type="button"
              onClick={() =>
                dispatch(
                  openConfirmDialog({
                    title: 'Souhaitez-vous supprimer le modèle de wagon ?',
                    actionText: 'Supprimer',
                    action: deleteLibraryWagon(vehiclesLibrary.editedWagon),
                    actionClass: 'danger',
                  }),
                )
              }
            >
              Supprimer
            </button>
          )}
        </div>
      </form>
    </Overlay>
  );
};

const mapStateToProps = (state: RootState) => ({
  initialValues: getVehiclesLibrary(state).editedWagon ?? initialFormValuesForNewWagonTemplates(),
});

const WagonTemplateForm = reduxForm<WagonTemplateFormData>({
  form: 'wagonTemplateForm',
  validate,
})(WagonTemplateDetails);

export default connect(mapStateToProps)(WagonTemplateForm);
